import React, {useEffect} from 'react';
import {Link} from 'react-router-dom';
import {useAppDispatch} from '../../hooks/redux';
import {appSlice} from '../../store/reducers/AppSlice';
import {statisticModelApi} from '../../api/StatisticModelsService';
import Loader from '../../components/ui/Loader';
import {aggregationLabel, periodTypeLabel} from '../../utils/statsModels';
import {TransactionType} from '../../model/TransactionType';
import {PlusIcon} from '@heroicons/react/24/solid';

const StatsModelsPage = () => {
    const dispatch = useAppDispatch();
    const {setTitle} = appSlice.actions;
    const {data: models, isLoading} = statisticModelApi.useFetchAllStatisticModelsQuery();
    const [deleteModel] = statisticModelApi.useDeleteStatisticModelMutation();

    useEffect(() => {
        dispatch(setTitle('Stats models'));
    });

    const handleDelete = async (id: number, name: string) => {
        if (!window.confirm(`Delete stats model "${name}"?`)) {
            return;
        }
        await deleteModel(id);
    };

    return (
        <div>
            <div className="bg-white px-3 py-2 flex items-center border-t border-gray-200 sm:px-4">
                <p className="text-sm text-gray-700">Saved statistic models</p>
                <Link
                    to="/stats/models/new"
                    title="New stats model"
                    aria-label="New stats model"
                    className="ml-auto inline-flex items-center justify-center rounded-full p-1.5 bg-green-100 text-green-800 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-green-500"
                >
                    <PlusIcon className="h-4 w-4"/>
                </Link>
            </div>

            {isLoading && <div className="relative min-h-[8rem]"><Loader/></div>}

            {!isLoading && (!models || models.length === 0) && (
                <p className="px-4 py-8 text-sm text-gray-500">No stats models yet. Create one to save a reusable statistic.</p>
            )}

            {models && models.length > 0 && (
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                    <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Period</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Aggregation</th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                    {models.map((model) => (
                        <tr key={model.id}>
                            <td className="px-4 py-3 text-sm font-medium text-gray-900">{model.name}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">
                                {model.transaction_type === TransactionType.Income ? 'Income' : 'Expense'}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-700">{periodTypeLabel(model.period_type)}</td>
                            <td className="px-4 py-3 text-sm text-gray-700">{aggregationLabel(model)}</td>
                            <td className="px-4 py-3 text-sm text-right whitespace-nowrap">
                                <Link to={`/stats/models/${model.id}`} className="text-indigo-700 hover:text-indigo-900 mr-3">Open</Link>
                                <Link to={`/stats/models/${model.id}/edit`} className="text-gray-700 hover:text-gray-900 mr-3">Edit</Link>
                                <button
                                    type="button"
                                    className="text-red-700 hover:text-red-900"
                                    onClick={() => handleDelete(model.id, model.name)}
                                >
                                    Delete
                                </button>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default StatsModelsPage;
